/**
 * Freshness bookkeeping for master data.
 *
 * Master lists (brands, seasons, categories, …) change perhaps weekly, but nothing in this app
 * checked the store before dispatching, so every mount refetched every list it needed — and
 * navigating table view → Create Product → back refetched them in both directions.
 *
 * This records when each master was last read so a fetch can be skipped while the copy in the
 * store is still good. It is intentionally NOT a data cache: the data itself continues to live in
 * Redux, and this module only answers "is that copy recent enough?".
 */

/** Masters are re-read after this long even if nothing invalidated them. */
export const DEFAULT_MASTER_TTL_MS = 5 * 60 * 1000;

/** Fetch params accepted by a master read; values are whatever the endpoint takes. */
export type MasterParams = Record<string, unknown>;

/** The subset of an axios request config this module inspects. */
export interface MasterRequestConfig {
  method?: string;
  url?: string;
}

const lastFetchedAt = new Map<string, number>();

/**
 * Serialise fetch params with stable key ordering, so the same request always yields one key.
 * @param value the params to serialise
 * @returns a key-order-independent string form
 */
const stableStringify = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'object') return String(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const source = value as Record<string, unknown>;
  return `{${Object.keys(source)
    .sort()
    .map((key) => `${key}:${stableStringify(source[key])}`)
    .join(',')}}`;
};

/**
 * Build the identity of a master read. Params are part of it because several masters are the same
 * endpoint under different arguments.
 * @param name the registered master name
 * @param params the fetch params
 * @returns the cache key
 */
export const buildMasterKey = (name: string, params?: MasterParams): string => `${name}|${stableStringify(params)}`;

/**
 * Whether a master was read recently enough to reuse.
 * @param key the cache key
 * @param ttlMs how long a read stays good
 * @returns true when the recorded read is still within the TTL
 */
export const isMasterFresh = (key: string, ttlMs: number = DEFAULT_MASTER_TTL_MS): boolean => {
  const readAt = lastFetchedAt.get(key);
  return typeof readAt === 'number' && Date.now() - readAt < ttlMs;
};

/**
 * Record that a master has just been read.
 * @param key the cache key
 */
export const markMasterFetched = (key: string): void => {
  lastFetchedAt.set(key, Date.now());
};

/**
 * Forget a single master, so its next read goes to the server.
 * @param name the registered master name
 */
export const invalidateMaster = (name: string): void => {
  const prefix = `${name}|`;
  [...lastFetchedAt.keys()].filter((key) => key.startsWith(prefix)).forEach((key) => lastFetchedAt.delete(key));
};

/**
 * Forget every master. Used when a master has been edited anywhere in the app, and on logout so a
 * different user never inherits the previous session's freshness.
 */
export const invalidateAllMasters = (): void => {
  lastFetchedAt.clear();
};

/** Any request path containing this segment is a master endpoint. */
const MASTER_PATH_MARKER = '/masters/';

/**
 * Master endpoints that READ over POST. Everything else non-GET under `/masters/` is treated as a
 * write. Erring this way is deliberate: mistaking a read for a write only costs a refetch, while
 * mistaking a write for a read would leave a stale master on screen for the whole TTL.
 */
const MASTER_READ_POST_PATHS: readonly string[] = ['/masters/fetch-categories', '/masters/fetch-type-based-category'];

/**
 * Drop all master freshness when a request looks like it edited a master.
 *
 * Hooked into the shared response interceptor rather than into each mutation thunk, so a master
 * edited anywhere in the app invalidates the cache without every call site having to remember to
 * say so.
 * @param config the axios request config of a successful response
 * @returns true when the cache was invalidated
 */
export const noteMasterMutation = (config?: MasterRequestConfig): boolean => {
  const method = String(config?.method || 'get').toLowerCase();
  if (method === 'get') return false;
  const [url = ''] = String(config?.url || '').split('?');
  if (!url.includes(MASTER_PATH_MARKER)) return false;
  if (MASTER_READ_POST_PATHS.some((path) => url.endsWith(path))) return false;
  invalidateAllMasters();
  return true;
};
